import React, { useState, useMemo, useEffect } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  useRepos,
  useRepoFindings,
  useRepoMetrics,
  useTriggerScan,
  useSuppressFinding,
  invalidateRepo,
  prefetchRepoFindings,
  queryKeys,
} from "../api/hooks";
import {
  ArrowLeft,
  ExternalLink,
  GitBranch,
  Clock,
  Play,
  Loader2,
  Download,
  RefreshCw,
  Shield,
  Package,
  Box,
  AlertTriangle,
  AlertCircle,
  Trash2,
  TrendingUp,
  GitPullRequest,
  Activity,
} from "lucide-react";
import Layout, { PageContainer, Section } from "../components/ui/Layout";
import StatsCard from "../components/ui/StatsCard";
import { SkeletonStats, SkeletonTable } from "../components/ui/Skeleton";
import FilterBar, { FilterState } from "../components/ui/FilterBar";
import FindingRow, { Finding, FindingCard } from "../components/ui/FindingRow";
import FindingDrawer from "../components/ui/FindingDrawer";
import ScanProgressModal from "../components/ScanProgressModal";
import SuppressModal, { SuppressPayload } from "../components/SuppressModal";
import SavedViewsDropdown from "../components/ui/SavedViewsDropdown";
import PriorityQueuePanel from "../components/ui/PriorityQueuePanel";
import { SeverityCount, Severity } from "../components/ui/SeverityBadge";
import SBOMTab from "../components/ui/SBOMTab";
import ContainerTab from "../components/ui/ContainerTab";
import PrScansTab from "../components/ui/PrScansTab";
import CodeHealthTab from "../components/ui/CodeHealthTab";
import TrendChart from "../components/ui/TrendChart";
import ExportReportModal from "../components/ExportReportModal";
import {
  type RepoSummary,
  type FindingRecord,
} from "../api/client";

import { Card, CardContent, CardHeader, CardTitle } from "../components/shadcn/card";
import { Badge } from "../components/shadcn/badge";
import { Button } from "../components/shadcn/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/shadcn/tabs";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../components/shadcn/table";
import { Separator } from "../components/shadcn/separator";
import { cn } from "../lib/utils";

type TabType = "findings" | "trends" | "sbom" | "container" | "pr-scans" | "code-health";

export default function RepoDetails() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const repoId = id || "";
  const qc = useQueryClient();
  const navigate = useNavigate();

  // Active tab from URL or default to findings
  const activeTab = (searchParams.get("tab") as TabType) || "findings";
  const setActiveTab = (tab: TabType) => {
    setSearchParams({ tab });
  };

  // State
  const [filters, setFilters] = useState<FilterState>({
    search: "",
    severities: [],
    tools: [],
    kinds: [],
    statuses: ["open"],
  });
  const [selectedFinding, setSelectedFinding] = useState<FindingRecord | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [suppressTarget, setSuppressTarget] = useState<FindingRecord | null>(null);
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [deletingRepo, setDeletingRepo] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 50;

  // Persist scan state in localStorage so it survives page refresh / modal reopen
  const scanStorageKey = `paxley_scan_${repoId}`;
  const [scanState, setScanStateRaw] = useState<{ jobId: string; startedAt: number } | null>(() => {
    try {
      const stored = localStorage.getItem(scanStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Validate stored state structure before using
        if (
          typeof parsed?.jobId === "string" && parsed.jobId &&
          typeof parsed?.startedAt === "number" && Number.isFinite(parsed.startedAt) &&
          Date.now() - parsed.startedAt < 30 * 60 * 1000
        ) {
          return parsed;
        }
        localStorage.removeItem(scanStorageKey);
      }
    } catch {}
    return null;
  });
  const scanJobId = scanState?.jobId ?? null;
  const scanStartedAt = scanState?.startedAt ?? null;
  const setScanJobId = (jobId: string | null) => {
    if (jobId) {
      const state = { jobId, startedAt: Date.now() };
      setScanStateRaw(state);
      localStorage.setItem(scanStorageKey, JSON.stringify(state));
    } else {
      setScanStateRaw(null);
      localStorage.removeItem(scanStorageKey);
    }
  };

  // Server-side fallback: if this page has no locally-tracked scan, ask the API
  // whether one is queued/running for this repo. Covers scans started from the
  // upload page, another tab, or another device — previously those showed the
  // empty "No findings yet" state while the scan was actually running.
  useEffect(() => {
    if (!repoId || scanJobId) return;
    let cancelled = false;
    (async () => {
      try {
        const { getActiveScan } = await import("../api/client");
        const active = await getActiveScan(repoId);
        if (cancelled || !active?.active || !active.job_id) return;
        const startedAt = active.started_at ? Date.parse(active.started_at) : Date.now();
        const state = {
          jobId: active.job_id,
          startedAt: Number.isFinite(startedAt) ? startedAt : Date.now(),
        };
        setScanStateRaw(state);
        try {
          localStorage.setItem(scanStorageKey, JSON.stringify(state));
        } catch {
          /* non-fatal */
        }
      } catch {
        /* no active scan endpoint / network issue — nothing to show */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [repoId, scanJobId, scanStorageKey]);

  // Queries
  const reposQuery = useRepos();
  const findingsQuery = useRepoFindings(repoId, {
    severity: filters.severities.length === 1 ? filters.severities[0] : undefined,
    status: filters.statuses.length === 1 ? filters.statuses[0] : undefined,
    q: filters.search || undefined,
    limit: pageSize,
    offset: page * pageSize,
  });

  const repo = reposQuery.data?.find((r) => r.id === repoId);

  // A scan that fails before ingest leaves no scan record, so the job state is
  // the only evidence it ran at all. Without this the page showed "No findings
  // yet — run a scan" after a failure, which reads as though nothing had been
  // attempted and sends people round the same loop again.
  const lastScanFailed = repo?.last_job_state === "failed" || repo?.last_job_state === "dead";
  const lastScanError = repo?.last_job_error || null;

  // Drop a stale "Scan Running" handoff once the job has actually finished.
  // The scan state is written when a scan starts and cleared by the progress
  // dialog; if the user navigated away before it finished, the button kept
  // advertising a scan that had already failed or completed.
  const lastJobSettled =
    repo?.last_job_state === "failed" ||
    repo?.last_job_state === "dead" ||
    repo?.last_job_state === "completed";
  useEffect(() => {
    if (lastJobSettled && scanJobId && !scanModalOpen) {
      setScanStateRaw(null);
      try {
        localStorage.removeItem(scanStorageKey);
      } catch {
        /* non-fatal */
      }
    }
  }, [lastJobSettled, scanJobId, scanModalOpen, scanStorageKey]);

  const allFindings = findingsQuery.data?.findings || [];
  const pagination = findingsQuery.data?.pagination;
  const totalFindings = pagination?.total ?? allFindings.length;
  const totalPages = Math.max(1, Math.ceil(totalFindings / pageSize));

  // Reset page when filters change
  const filtersKey = JSON.stringify(filters);
  React.useEffect(() => { setPage(0); }, [filtersKey]);

  // Filter findings — only apply client-side filters for criteria NOT sent to server
  // Server already handles: severity (single), status (single), search (q)
  // Client handles: multi-severity, multi-status, kind, tool, CVE, KEV, minPriority
  const filteredFindings = useMemo(() => {
    return allFindings.filter((f) => {
      // Multi-severity filter (server only supports single; apply client-side when multi)
      if (filters.severities.length > 1) {
        if (!filters.severities.includes(f.severity as Severity)) return false;
      }

      // Kind
      if (filters.kinds.length > 0) {
        const kind = getKind(f);
        if (!filters.kinds.includes(kind)) return false;
      }

      // Multi-status filter (server only supports single)
      if (filters.statuses.length > 1) {
        if (!filters.statuses.includes(f.status as "open" | "fixed" | "suppressed")) return false;
      }

      // Tool
      if (filters.tools.length > 0) {
        if (!filters.tools.includes(f.tool)) return false;
      }

      // CVE filter
      if (filters.hasCve) {
        if (!f.cve_id) return false;
      }

      // KEV filter
      if (filters.isKev) {
        if (!f.is_kev) return false;
      }

      // Min priority filter
      if (filters.minPriority) {
        if ((f.priority_score || 0) < filters.minPriority) return false;
      }

      return true;
    });
  }, [allFindings, filters]);

  // True when filters are applied that the server doesn't know about — the
  // server-side total no longer matches what's actually rendered, so the
  // pagination footer needs to switch to showing the page-local count.
  const isClientFilterActive =
    filters.severities.length > 1 ||
    filters.kinds.length > 0 ||
    filters.statuses.length > 1 ||
    filters.tools.length > 0 ||
    filters.hasCve ||
    filters.isKev ||
    Boolean(filters.minPriority);

  // Stats from repo summary (accurate totals, not just current page)
  const stats = useMemo(() => {
    if (repo) {
      return {
        total: (repo.open_count ?? 0) + (repo.fixed_count ?? 0) + (repo.suppressed_count ?? 0),
        open: repo.open_count ?? 0,
        fixed: repo.fixed_count ?? 0,
        suppressed: repo.suppressed_count ?? 0,
        critical: repo.open_critical ?? 0,
        high: repo.open_high ?? 0,
        medium: repo.open_medium ?? 0,
        low: repo.open_low ?? 0,
        // P0 Metrics — fallback to page data when repo summary lacks them
        kevCount: allFindings.filter((f) => f.status === "open" && f.is_kev).length,
        highPriority: allFindings.filter((f) => f.status === "open" && (f.priority_score || 0) >= 70).length,
        newFindings: allFindings.filter((f) => f.status === "open" && f.is_new).length,
        withCve: allFindings.filter((f) => f.status === "open" && f.cve_id).length,
      };
    }
    // Fallback: derive from current page (inaccurate but better than nothing)
    const open = allFindings.filter((f) => f.status === "open");
    return {
      total: allFindings.length,
      open: open.length,
      fixed: allFindings.filter((f) => f.status === "fixed").length,
      suppressed: allFindings.filter((f) => f.status === "suppressed").length,
      critical: open.filter((f) => f.severity === "critical").length,
      high: open.filter((f) => f.severity === "high").length,
      medium: open.filter((f) => f.severity === "medium").length,
      low: open.filter((f) => f.severity === "low").length,
      kevCount: open.filter((f) => f.is_kev).length,
      highPriority: open.filter((f) => (f.priority_score || 0) >= 70).length,
      newFindings: open.filter((f) => f.is_new).length,
      withCve: open.filter((f) => f.cve_id).length,
    };
  }, [repo, allFindings]);

  // Available tools for filter
  const availableTools = useMemo(() => {
    return [...new Set(allFindings.map((f) => f.tool))];
  }, [allFindings]);

  // Scan mutation
  const scanMutation = useTriggerScan(repoId);

  // Suppress mutation (optimistic)
  const suppressMutation = useSuppressFinding(repoId);

  const handleScanComplete = async () => {
    await invalidateRepo(qc, repoId);
    setScanJobId(null);
    setScanModalOpen(false);
  };

  // Also clear job ID when modal closes after completion/failure
  const handleScanModalClose = () => {
    setScanModalOpen(false);
    // Don't clear jobId here — keep it so "Scan Running" button shows
    // It gets cleared when scan completes via handleScanComplete
  };

  const handleFindingClick = (finding: FindingRecord) => {
    setSelectedFinding(finding);
    setDrawerOpen(true);
  };

  const handleSuppress = (finding: FindingRecord) => {
    setSuppressTarget(finding);
    setDrawerOpen(false);
  };

  const repoName = repo?.url.replace("https://github.com/", "") || "Repository";
  const isLoading = reposQuery.isLoading || findingsQuery.isLoading;
  const queryError = reposQuery.error || findingsQuery.error;

  // Helper to determine finding kind
  const getKind = (f: FindingRecord): "sast" | "sca" | "secret" | "iac" | "license" | "container" => {
    // The scanner records each finding's kind; trust it before guessing from
    // the tool name, so lanes added later (vendored libraries, Java archives,
    // declared runtimes) are not mis-filed as code findings.
    const recorded = String(f.location_data?.kind || "").toLowerCase();
    if (["sast", "sca", "secret", "iac", "license", "container"].includes(recorded)) {
      return recorded as "sast" | "sca" | "secret" | "iac" | "license" | "container";
    }
    if (f.tool === "gitleaks") return "secret";
    if (f.tool === "osv-scanner" || (f.tool || "").startsWith("paxley-") && f.tool !== "paxley-hygiene") return "sca";
    if (f.tool === "codeql") return "sast"; // CodeQL findings map to SAST
    if (f.tool === "trivy") {
      // Check location_data for type hints
      const locType = (f.location_data?.type as string)?.toLowerCase() || "";
      if (locType.includes("license")) return "license";
      if (locType.includes("container") || locType.includes("image")) return "container";
      return "iac";
    }
    if (f.tool === "kics") return "iac";
    return "sast";
  };

  // Convert API finding to component Finding type
  const toFinding = (f: FindingRecord): Finding => ({
    id: f.id,
    severity: f.severity as Severity,
    title: f.title,
    ruleId: (f.location_data?.rule_id as string) || undefined,
    tool: f.tool,
    kind: getKind(f),
    file: f.file_path || undefined,
    line: f.start_line || undefined,
    endLine: f.end_line || undefined,
    status: f.status as "open" | "fixed" | "suppressed",
    // Code snippet for developer context
    snippet: (f.location_data?.code_snippet as string) ||
             (f.location_data?.snippet as string) || undefined,
    codeContext: (f.location_data?.code_context as string) || undefined,
    // P0 Features
    priorityScore: f.priority_score,
    riskScore: f.risk_score,
    isKev: f.is_kev,
    epssScore: f.epss_score,
    cvssScore: f.cvss_score,
    cvssVector: (f.location_data?.cvss_vector as string) || undefined,
    cveId: f.cve_id,
    cweId: (f.location_data?.cwe as string[])?.join(", ") ||
           (f.location_data?.cwe as string) || undefined,
    isNew: f.is_new,
    isReachable: f.is_reachable,
    // Enhanced fields
    description: (f.location_data?.description as string) || undefined,
    fixGuidance: (f.location_data?.fix_guidance as string) || undefined,
    owasp: (f.location_data?.owasp as string[]) || undefined,
    owaspNames: (f.location_data?.owasp_names as string[]) || undefined,
    confidence: (f.location_data?.confidence as string) || undefined,
    language: (f.location_data?.language as string) || undefined,
    fixVersion: (f.location_data?.fix_version as string) || undefined,
    references: (f.location_data?.references as string[]) || undefined,
  });

  const drawerFinding = selectedFinding
    ? {
        id: selectedFinding.id,
        severity: selectedFinding.severity as Severity,
        title: selectedFinding.title,
        description: (selectedFinding.location_data?.description as string) || undefined,
        ruleId: (selectedFinding.location_data?.rule_id as string) || undefined,
        tool: selectedFinding.tool,
        kind: getKind(selectedFinding),
        file: selectedFinding.file_path || undefined,
        line: selectedFinding.start_line || undefined,
        endLine: selectedFinding.end_line || undefined,
        snippet: (selectedFinding.location_data?.code_snippet as string) ||
                 (selectedFinding.location_data?.snippet as string) || undefined,
        codeContext: (selectedFinding.location_data?.code_context as string) || undefined,
        status: selectedFinding.status as "open" | "fixed" | "suppressed",
        cweId: (selectedFinding.location_data?.cwe_id as string) ||
               ((selectedFinding.location_data?.cwe as string[])?.join(", ")) || undefined,
        cveId: selectedFinding.cve_id || undefined,
        fixGuidance: (selectedFinding.location_data?.fix_guidance as string) || undefined,
        firstSeen: selectedFinding.first_seen_at,
        lastSeen: selectedFinding.last_seen_at,
        priorityScore: selectedFinding.priority_score,
        riskScore: selectedFinding.risk_score,
        isKev: selectedFinding.is_kev,
        epssScore: selectedFinding.epss_score,
        cvssScore: selectedFinding.cvss_score,
        isNew: selectedFinding.is_new,
        isReachable: selectedFinding.is_reachable,
        recommendation: selectedFinding.recommendation,
        introducedAt: selectedFinding.introduced_at,
        fixedAt: selectedFinding.fixed_at,
        dataflow: (selectedFinding.location_data?.dataflow as Array<{ file: string; line: number; message: string }>) || undefined,
      }
    : null;

  return (
    <Layout>
      <PageContainer>
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/dashboard/repos"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-5"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Repositories
          </Link>

          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-semibold text-foreground flex items-center gap-2.5">
                {repoName}
                {repo && (
                  <a
                    href={repo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition-colors"
                    aria-label="Open repository on GitHub"
                  >
                    <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  </a>
                )}
              </h1>
              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                {repo?.default_branch && (
                  <Badge variant="outline" className="gap-1 font-normal">
                    <GitBranch className="h-3.5 w-3.5" />
                    {repo.default_branch}
                  </Badge>
                )}
                {repo?.last_scan_at && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    Last scan: {new Date(repo.last_scan_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <SavedViewsDropdown
                repoId={repoId}
                currentFilters={filters}
                onApplyView={setFilters}
              />
              <Button
                variant="outline"
                onClick={() => setExportModalOpen(true)}
                aria-label="Export report"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => findingsQuery.refetch()}
                disabled={findingsQuery.isFetching}
                aria-label="Refresh findings"
              >
                <RefreshCw className={cn("h-4 w-4", findingsQuery.isFetching && "animate-spin")} />
              </Button>
              {scanJobId && !scanModalOpen ? (
                <Button
                  variant="outline"
                  className="border-primary/30 text-primary"
                  onClick={() => setScanModalOpen(true)}
                >
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Scan Running — View Progress
                </Button>
              ) : (
                <Button
                  onClick={() => scanMutation.mutate({ scanType: "baseline" }, {
                    onSuccess: (data) => {
                      setScanJobId(data?.job_id || null);
                      setScanModalOpen(true);
                    },
                  })}
                  disabled={scanMutation.isPending || (!!scanJobId && scanModalOpen)}
                >
                  {scanMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Play className="h-4 w-4 mr-2" />
                  )}
                  Run Scan
                </Button>
              )}
              {/* Uploaded projects can be removed. Previously there was no way
                  to delete one, so a repository left behind by a failed upload
                  permanently occupied a slot in the plan's allowance. */}
              {repo?.url?.startsWith("manual://") && (
                <Button
                  variant="outline"
                  disabled={deletingRepo}
                  onClick={async () => {
                    const label = repo.url.replace("manual://", "");
                    if (
                      !window.confirm(
                        `Delete "${label}"? This removes the project and all of its findings, scans and reports. This cannot be undone.`
                      )
                    ) {
                      return;
                    }
                    setDeletingRepo(true);
                    try {
                      const { deleteRepo } = await import("../api/client");
                      await deleteRepo(repoId);
                      await invalidateRepo(qc, repoId);
                      qc.invalidateQueries({ queryKey: queryKeys.repos.all });
                      navigate("/dashboard/repos");
                    } catch (e) {
                      setDeleteError(
                        e instanceof Error ? e.message : "Could not delete this project."
                      );
                    } finally {
                      setDeletingRepo(false);
                    }
                  }}
                >
                  {deletingRepo ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Delete
                </Button>
              )}
            </div>
          </div>
        </div>

        {deleteError && (
          <Card className="p-4 border-critical/30 bg-critical/10">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-none text-critical" />
              <div className="text-sm text-critical">{deleteError}</div>
            </div>
          </Card>
        )}

        {/* Last scan failed — say so, and say why. The worker records an
            accurate reason (unreadable archive, clone failure); before this it
            was never shown anywhere in the app once the progress dialog closed. */}
        {lastScanFailed && (
          <Card className="p-4 border-critical/30 bg-critical/10">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-none text-critical" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-critical">
                  The last scan did not complete
                </div>
                <div className="text-sm text-muted-foreground mt-1 break-words">
                  {lastScanError ||
                    "The scan failed before it could produce results. Please try again, or upload the archive once more."}
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  Any findings shown below are from an earlier successful scan.
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as TabType)}
          className="space-y-6"
        >
          <TabsList className="bg-surface1 border border-border/40 rounded-lg p-1">
            <TabsTrigger value="findings" className="gap-2">
              <Shield className="h-4 w-4" aria-hidden="true" />
              Findings
            </TabsTrigger>
            <TabsTrigger value="trends" className="gap-2">
              <TrendingUp className="h-4 w-4" aria-hidden="true" />
              Trends
            </TabsTrigger>
            <TabsTrigger value="sbom" className="gap-2">
              <Package className="h-4 w-4" aria-hidden="true" />
              SBOM
            </TabsTrigger>
            <TabsTrigger value="container" className="gap-2">
              <Box className="h-4 w-4" aria-hidden="true" />
              Container
            </TabsTrigger>
            <TabsTrigger value="pr-scans" className="gap-2">
              <GitPullRequest className="h-4 w-4" aria-hidden="true" />
              PR Scans
            </TabsTrigger>
            <TabsTrigger value="code-health" className="gap-2">
              <Activity className="h-4 w-4" aria-hidden="true" />
              Code Health
            </TabsTrigger>
          </TabsList>

          {/* Trends Tab */}
          <TabsContent value="trends">
            <TrendsTab repoId={repoId} />
          </TabsContent>

          {/* SBOM Tab */}
          <TabsContent value="sbom">
            <SBOMTab repoId={repoId} />
          </TabsContent>

          {/* Container Tab */}
          <TabsContent value="container">
            <ContainerTab repoId={repoId} />
          </TabsContent>

          {/* PR Scans Tab */}
          <TabsContent value="pr-scans">
            <PrScansTab repoId={repoId} />
          </TabsContent>

          {/* Code Health Tab */}
          <TabsContent value="code-health">
            <CodeHealthTab repoId={repoId} />
          </TabsContent>

          {/* Findings Tab */}
          <TabsContent value="findings">
            {isLoading ? (
              <div className="space-y-6">
                <SkeletonStats count={4} />
                <SkeletonTable rows={8} columns={5} />
              </div>
            ) : queryError ? (
              <Card className="p-8 text-center">
                <CardContent className="space-y-3 p-0">
                  <AlertTriangle className="h-10 w-10 text-red-500 mx-auto" />
                  <div className="text-lg font-medium text-foreground">Failed to load findings</div>
                  <p className="text-sm text-muted-foreground">
                    {(queryError as Error)?.message || "Unable to fetch data. Please try again."}
                  </p>
                  <Button onClick={() => { reposQuery.refetch(); findingsQuery.refetch(); }} className="mt-2">
                    Retry
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Open</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-red-500">{stats.open.toLocaleString()}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Fixed</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-green-500">{stats.fixed.toLocaleString()}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Suppressed</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-muted-foreground">{stats.suppressed.toLocaleString()}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">By Severity</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="critical">{stats.critical} Critical</Badge>
                        <Badge variant="high">{stats.high} High</Badge>
                        <Badge variant="medium">{stats.medium} Medium</Badge>
                        <Badge variant="low">{stats.low} Low</Badge>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* P0 Risk Insights */}
                {(stats.kevCount > 0 || stats.highPriority > 0 || stats.newFindings > 0) && (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {stats.kevCount > 0 && (
                      <Card
                        className={cn(
                          "border-l-4 cursor-pointer transition-all hover:ring-2 hover:ring-red-500/50",
                          filters.isKev
                            ? "border-l-red-500 ring-2 ring-red-500/50 bg-red-500/10"
                            : "border-l-red-500 bg-red-500/5"
                        )}
                        onClick={() => setFilters({ ...filters, isKev: true, statuses: ["open"] })}
                      >
                        <CardContent className="p-5">
                          <div className="flex items-center gap-2 text-sm font-medium text-red-500 mb-1">
                            <span className="animate-pulse">🔥</span> KEV Vulnerabilities
                            {filters.isKev && <span className="text-xs">(filtered)</span>}
                          </div>
                          <div className="text-2xl font-bold text-red-500">{stats.kevCount}</div>
                          <div className="text-xs text-muted-foreground mt-1">Click to filter - Known exploited in the wild</div>
                        </CardContent>
                      </Card>
                    )}
                    {stats.highPriority > 0 && (
                      <Card
                        className={cn(
                          "border-l-4 cursor-pointer transition-all hover:ring-2 hover:ring-orange-500/50",
                          filters.minPriority
                            ? "border-l-orange-500 ring-2 ring-orange-500/50 bg-orange-500/5"
                            : "border-l-orange-500"
                        )}
                        onClick={() => setFilters({ ...filters, minPriority: 70, statuses: ["open"] })}
                      >
                        <CardContent className="p-5">
                          <div className="flex items-center gap-2 text-sm font-medium text-orange-500 mb-1">
                            ⚡ High Priority
                            {filters.minPriority && <span className="text-xs">(filtered)</span>}
                          </div>
                          <div className="text-2xl font-bold text-orange-500">{stats.highPriority}</div>
                          <div className="text-xs text-muted-foreground mt-1">Click to filter - Priority score ≥ 70</div>
                        </CardContent>
                      </Card>
                    )}
                    {stats.newFindings > 0 && (
                      <Card className="border-l-4 border-l-primary">
                        <CardContent className="p-5">
                          <div className="flex items-center gap-2 text-sm font-medium text-primary mb-1">
                            ✨ New Findings
                          </div>
                          <div className="text-2xl font-bold text-primary">{stats.newFindings}</div>
                          <div className="text-xs text-muted-foreground mt-1">Introduced in latest scan</div>
                        </CardContent>
                      </Card>
                    )}
                    {stats.withCve > 0 && (
                      <Card
                        className={cn(
                          "border-l-4 cursor-pointer transition-all hover:ring-2 hover:ring-yellow-500/50",
                          filters.hasCve
                            ? "border-l-yellow-500 ring-2 ring-yellow-500/50 bg-yellow-500/5"
                            : "border-l-yellow-500"
                        )}
                        onClick={() => setFilters({ ...filters, hasCve: true, statuses: ["open"] })}
                      >
                        <CardContent className="p-5">
                          <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-1">
                            🛡️ With CVE
                            {filters.hasCve && <span className="text-xs text-yellow-500">(filtered)</span>}
                          </div>
                          <div className="text-2xl font-bold text-foreground">{stats.withCve}</div>
                          <div className="text-xs text-muted-foreground mt-1">Click to filter - Linked to CVE database</div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}

                {/* Priority Queue - Top issues to fix first */}
                <PriorityQueuePanel
                  repoId={repoId}
                  onFindingClick={(f) => handleFindingClick(f as FindingRecord)}
                  limit={5}
                />

                {/* Filters */}
                <FilterBar
                  filters={filters}
                  onChange={setFilters}
                  availableTools={availableTools}
                  totalCount={allFindings.length}
                  filteredCount={filteredFindings.length}
                />

                {/* Findings Table */}
                <Section>
                  {filteredFindings.length === 0 ? (
                    <Card className="p-12 text-center">
                      <CardContent className="p-0">
                        <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-green-500/10 text-green-500 ring-1 ring-green-500/20 mx-auto mb-4">
                          <Shield className="h-7 w-7" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground">
                          {allFindings.length === 0
                            ? lastScanFailed
                              ? "The last scan did not complete"
                              : "No findings yet"
                            : "No findings match your filters"}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-2">
                          {allFindings.length === 0
                            ? lastScanFailed
                              ? lastScanError ||
                                "The most recent scan failed before it could produce results."
                              : "Run a scan to detect vulnerabilities"
                            : isClientFilterActive && totalPages > 1
                            ? `No matches on this page — there are ${totalFindings} total findings across ${totalPages} pages. Try a different page or adjust your filters.`
                            : "Try adjusting your filter criteria"}
                        </p>
                        {isClientFilterActive && totalPages > 1 && (
                          <div className="flex items-center justify-center gap-2 mt-4">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={page === 0}
                              onClick={() => setPage((p) => Math.max(0, p - 1))}
                              aria-label="Previous page"
                            >
                              Previous
                            </Button>
                            <span className="text-sm text-muted-foreground tabular-nums">
                              Page {page + 1} of {totalPages}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={page >= totalPages - 1}
                              onClick={() => setPage((p) => p + 1)}
                              aria-label="Next page"
                            >
                              Next
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                    <>
                      {/* Desktop table */}
                      <Card className="overflow-hidden hidden md:block">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead className="w-1 p-0"></TableHead>
                              <TableHead className="text-[11px] font-semibold uppercase tracking-wider">
                                Finding
                              </TableHead>
                              <TableHead className="text-[11px] font-semibold uppercase tracking-wider hidden lg:table-cell">
                                Engine
                              </TableHead>
                              <TableHead className="text-[11px] font-semibold uppercase tracking-wider">
                                Status
                              </TableHead>
                              <TableHead className="text-[11px] font-semibold uppercase tracking-wider hidden md:table-cell">
                                Severity
                              </TableHead>
                              <TableHead className="w-24"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody className="stagger-fade-in">
                            {filteredFindings.map((f) => (
                              <FindingRow
                                key={f.id}
                                finding={toFinding(f)}
                                onClick={() => handleFindingClick(f)}
                                onSuppress={() => handleSuppress(f)}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </Card>

                      {/* Mobile cards */}
                      <div className="space-y-3 md:hidden">
                        {filteredFindings.map((f) => (
                          <FindingCard
                            key={f.id}
                            finding={toFinding(f)}
                            onClick={() => handleFindingClick(f)}
                            onSuppress={() => handleSuppress(f)}
                          />
                        ))}
                      </div>

                      {/* Pagination Controls */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                          <p className="text-sm text-muted-foreground">
                            {isClientFilterActive ? (
                              <>
                                Showing {filteredFindings.length} of {allFindings.length} on this page
                                {" "}<span className="opacity-70">(filtered from {totalFindings} total)</span>
                              </>
                            ) : (
                              <>Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalFindings)} of {totalFindings} findings</>
                            )}
                          </p>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={page === 0}
                              onClick={() => setPage((p) => Math.max(0, p - 1))}
                              aria-label="Previous page"
                            >
                              Previous
                            </Button>
                            <span className="text-sm text-muted-foreground tabular-nums">
                              Page {page + 1} of {totalPages}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={page >= totalPages - 1}
                              onClick={() => setPage((p) => p + 1)}
                              aria-label="Next page"
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </Section>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </PageContainer>

      {/* Finding Drawer */}
      <FindingDrawer
        finding={drawerFinding}
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedFinding(null);
        }}
        onSuppress={(f) => {
          const original = allFindings.find((af) => af.id === f.id);
          if (original) handleSuppress(original);
        }}
      />

      {/* Suppress Modal */}
      <SuppressModal
        open={Boolean(suppressTarget)}
        finding={suppressTarget}
        loading={suppressMutation.isPending}
        error={suppressMutation.error?.message || null}
        onClose={() => setSuppressTarget(null)}
        onSubmit={(payload) => {
          if (!suppressTarget) return;
          suppressMutation.mutate(
            { findingId: suppressTarget.id, reason: payload.reason, expires_at: payload.expires_at },
            { onSuccess: () => setSuppressTarget(null) }
          );
        }}
      />

      {/* Export Report Modal */}
      <ExportReportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        repoId={repoId}
        repoName={repoName}
      />

      {/* Scan Progress Modal */}
      <ScanProgressModal
        isOpen={scanModalOpen}
        onComplete={handleScanComplete}
        onClose={handleScanModalClose}
        onFinished={() => setScanJobId(null)}
        repoName={repoName}
        jobId={scanJobId}
        startedAt={scanStartedAt}
      />
    </Layout>
  );
}

// ── Trends Tab ──────────────────────────────────────────────────────

function TrendsTab({ repoId }: { repoId: string }) {
  const [days, setDays] = React.useState(90);
  const metricsQuery = useRepoMetrics(repoId, days);

  return (
    <div className="space-y-6">
      {/* Day Range Selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">Security Trends</h2>
        <div className="flex gap-1 rounded-lg bg-muted p-1">
          {([30, 60, 90] as const).map((d) => (
            <Button
              key={d}
              variant={days === d ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setDays(d)}
              className={cn(
                "px-3 py-1.5 text-xs font-semibold",
                days === d && "shadow-sm"
              )}
            >
              {d}d
            </Button>
          ))}
        </div>
      </div>

      {metricsQuery.isLoading && (
        <Card className="p-12 text-center">
          <CardContent className="p-0">
            <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Loading trend data...</p>
          </CardContent>
        </Card>
      )}

      {metricsQuery.error && (
        <Card className="p-8 text-center">
          <CardContent className="p-0 space-y-3">
            <AlertTriangle className="h-8 w-8 text-red-500 mx-auto" />
            <p className="text-sm text-muted-foreground">Failed to load metrics. Please try again.</p>
            <Button onClick={() => metricsQuery.refetch()} size="sm" className="mt-3">
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {metricsQuery.data && <TrendChart data={metricsQuery.data} />}
    </div>
  );
}
